import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

interface AuditLogFiltersProps {
  filters: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    action?: string;
    resourceType?: string;
    phiOnly?: boolean;
    search?: string;
  };
  availableActions: string[];
  availableUsers: { id: string; email: string }[];
  onFiltersChange: (filters: AuditLogFiltersProps['filters']) => void;
  onReset: () => void;
}

export function AuditLogFilters({
  filters,
  availableActions,
  availableUsers,
  onFiltersChange,
  onReset,
}: AuditLogFiltersProps) {
  const { t } = useTranslation();

  const hasActiveFilters =
    filters.startDate ||
    filters.endDate ||
    filters.userId ||
    filters.action ||
    filters.resourceType ||
    filters.phiOnly ||
    filters.search;

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('audit.searchPlaceholder')}
          value={filters.search || ''}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="pl-10"
        />
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap gap-4">
        {/* Date Range */}
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => onFiltersChange({ ...filters, startDate: e.target.value })}
            className="w-40"
          />
          <span className="text-muted-foreground">-</span>
          <Input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => onFiltersChange({ ...filters, endDate: e.target.value })}
            className="w-40"
          />
        </div>

        {/* User Filter */}
        <select
          value={filters.userId || ''}
          onChange={(e) => onFiltersChange({ ...filters, userId: e.target.value || undefined })}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">{t('audit.allUsers')}</option>
          {availableUsers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.email}
            </option>
          ))}
        </select>

        {/* Action Filter */}
        <select
          value={filters.action || ''}
          onChange={(e) => onFiltersChange({ ...filters, action: e.target.value || undefined })}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">{t('audit.allActions')}</option>
          {availableActions.map((action) => (
            <option key={action} value={action}>
              {action}
            </option>
          ))}
        </select>

        {/* PHI Only Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.phiOnly || false}
            onChange={(e) => onFiltersChange({ ...filters, phiOnly: e.target.checked })}
            className="h-4 w-4 rounded border-input"
          />
          <span className="text-sm">{t('audit.phiAccess')}</span>
        </label>

        {/* Reset Button */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onReset}>
            <X className="mr-1 h-4 w-4" />
            {t('common.refresh')}
          </Button>
        )}
      </div>
    </div>
  );
}
